import { useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  deleteDocument,
  deleteFolder,
  type ClipboardDocument,
} from "@/features/clipboard/api/clipboard";
import {
  removeDocsFromClipboardCache,
  removeFolderFromClipboardCache,
  useClipboardAgentSearchQuery,
  useClipboardTreeQuery,
  useInvalidateClipboard,
} from "@/features/clipboard/clipboardQueries";
import { ConfirmDialog, type ConfirmRequest } from "@/features/clipboard/components/ConfirmDialog";
import { SharePanel } from "@/features/clipboard/components/SharePanel";
import { useClipboardSelection } from "@/features/clipboard/context/ClipboardSelectionContext";
import { useClipboardListing } from "@/features/clipboard/hooks/useClipboardListing";
import { useClipboardOpenPreview } from "@/features/clipboard/hooks/useClipboardOpenPreview";
import { useClipboardRoute } from "@/features/clipboard/hooks/useClipboardRoute";
import { clipboardDocumentFileType } from "@/features/clipboard/utils/documentFileType";
import type { SortColumn } from "@/features/clipboard/utils/sortListing";
import { clipboardDocumentUrl, clipboardFolderUrl } from "@/features/clipboard/utils/urls";
import { activateRowOnKey } from "@/utils/activateRowOnKey";
import { formatDate } from "@/utils/format";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type ClipboardFileTableProps = {
  dragActive: boolean;
  onDragOver: (event: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (event: React.DragEvent) => void;
  onError: (message: string) => void;
};

export const ClipboardFileTable = ({
  dragActive,
  onDragOver,
  onDragLeave,
  onDrop,
  onError,
}: ClipboardFileTableProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invalidateClipboard = useInvalidateClipboard();
  const { path, sharedOwnerId, routeSearchId, routeDocumentId } = useClipboardRoute();
  const {
    selected,
    selectedFolders,
    toggleSelect,
    toggleSelectFolder,
    clearSelection,
    setSelection,
  } = useClipboardSelection();
  const {
    clipboardPreviewOpen,
    setClipboardPreview,
    inspectedDoc,
    setInspectedDoc,
  } = useTreeViewOutletContext();
  const [sharingFolder, setSharingFolder] = useState<string | null>(null);
  const [sharingDoc, setSharingDoc] = useState<{ id: string; name: string } | null>(null);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const openPreview = useClipboardOpenPreview(onError);

  const {
    data: tree,
    isLoading: treeLoading,
  } = useClipboardTreeQuery(path, sharedOwnerId, !!user);
  const activeSearchQuery = useClipboardAgentSearchQuery(routeSearchId, !!user);
  const activeSearch = activeSearchQuery.data;

  const {
    sortColumn,
    sortDirection,
    rows,
    visibleRows,
    visibleCount,
    toggleSort,
    ariaSort,
    listScrollRef,
    sentinelRef,
  } = useClipboardListing({
    tree,
    activeSearch,
    routeSearchId,
    path,
    sharedOwnerId,
  });

  // Sync inspected row from document URL.
  const routeDocuments = routeSearchId ? activeSearch?.documents : tree?.documents;
  useEffect(() => {
    if (!routeDocumentId) {
      setInspectedDoc(null);
      setClipboardPreview(null);
      return;
    }
    if (!routeDocuments) return;
    const document = routeDocuments.find((item) => item.id === routeDocumentId);
    if (!document) return;
    if (inspectedDoc?.id !== document.id) {
      setInspectedDoc({ ...document, folder_path: path });
    }
  }, [
    inspectedDoc?.id,
    path,
    routeDocumentId,
    setClipboardPreview,
    setInspectedDoc,
    routeDocuments,
  ]);

  // Keep details panel in sync while indexing advances.
  useEffect(() => {
    if (!tree || !inspectedDoc) return;
    const fresh = tree.documents.find((doc) => doc.id === inspectedDoc.id);
    if (
      fresh &&
      (fresh.content_status !== inspectedDoc.content_status ||
        fresh.index_status !== inspectedDoc.index_status ||
        fresh.title !== inspectedDoc.title ||
        fresh.page_count !== inspectedDoc.page_count)
    ) {
      setInspectedDoc({ ...fresh, folder_path: path });
    }
  }, [tree, inspectedDoc, path, setInspectedDoc]);

  const inspect = useCallback(
    (doc: ClipboardDocument) => {
      const documentPath = doc.folder_path ?? path;
      // A file shared with us (surfaced at root) carries its owner id; open it
      // against that owner so the backend re-checks the document share.
      const ownerForDoc = doc.owner_id ?? sharedOwnerId;
      setInspectedDoc({ ...doc, folder_path: documentPath });
      navigate(
        clipboardDocumentUrl(doc.id, documentPath, ownerForDoc, routeSearchId),
      );
      if (clipboardPreviewOpen) void openPreview(doc);
    },
    [
      clipboardPreviewOpen,
      navigate,
      openPreview,
      path,
      routeSearchId,
      setInspectedDoc,
      sharedOwnerId,
    ],
  );

  const requestDeleteFolder = useCallback(
    (folderName: string, target: string, docCount: number) => {
      if (!user) return;
      setConfirm({
        title: `Delete folder “${folderName}”?`,
        description: (
          <>
            This deletes the folder and all {docCount} document
            {docCount === 1 ? "" : "s"} inside — including their text and every
            search index — from Paperclip’s live systems. It can’t be recovered
            through Paperclip; encrypted provider backups may persist
            temporarily under their retention policies.
          </>
        ),
        confirmLabel: "Delete folder",
        onConfirm: () => {
          const rollback = removeFolderFromClipboardCache(queryClient, folderName);
          deleteFolder(target)
            .then(() => invalidateClipboard())
            .catch((err) => {
              rollback();
              onError(err instanceof Error ? err.message : "Delete failed");
            });
        },
      });
    },
    [invalidateClipboard, onError, queryClient, user],
  );

  const requestDeleteDoc = useCallback(
    (doc: ClipboardDocument) => {
      if (!user) return;
      const label = doc.filename || doc.title || "this file";
      const isLink = doc.source_type === "link";
      setConfirm({
        title: `Delete “${label}”?`,
        description: isLink ? (
          <>
            This removes the corpus link from your Clipboard. The original paper
            stays available in the shared corpus.
          </>
        ) : (
          <>
            This deletes the file along with its extracted text and every search
            index from Paperclip’s live systems. It can’t be recovered through
            Paperclip; encrypted provider backups may persist temporarily under
            their retention policies.
          </>
        ),
        confirmLabel: "Delete",
        onConfirm: () => {
          // Drop the row immediately so deletion feels instant; the backend
          // delete + reindex runs in the background and we reconcile on
          // settle (rolling the row back only if the request fails).
          if (inspectedDoc?.id === doc.id) setInspectedDoc(null);
          const rollback = removeDocsFromClipboardCache(queryClient, [doc.id]);
          deleteDocument(doc.id)
            .then(() => invalidateClipboard())
            .catch((err) => {
              rollback();
              invalidateClipboard();
              onError(err instanceof Error ? err.message : "Delete failed");
            });
        },
      });
    },
    [
      inspectedDoc?.id,
      invalidateClipboard,
      onError,
      queryClient,
      setInspectedDoc,
      user,
    ],
  );

  // Everything in the current view that can be selected: own folders (not in
  // search results) and every document row.
  const selectableFolderNames = useMemo(
    () =>
      routeSearchId
        ? []
        : rows.flatMap((row) => (row.kind === "folder" && !row.folder.shared ? [row.folder.name] : [])),
    [rows, routeSearchId],
  );
  const selectableDocIds = useMemo(
    () => rows.flatMap((row) => (row.kind === "doc" ? [row.doc.id] : [])),
    [rows],
  );
  const selectableCount = selectableFolderNames.length + selectableDocIds.length;
  const selectedCount = selected.size + selectedFolders.size;
  const allSelected = selectableCount > 0 && selectedCount >= selectableCount;
  const someSelected = selectedCount > 0;

  const sortIcon = (column: SortColumn) =>
    sortColumn === column ? (
      sortDirection === "asc" ? (
        <ArrowUp className="h-3 w-3" aria-hidden />
      ) : (
        <ArrowDown className="h-3 w-3" aria-hidden />
      )
    ) : null;

  return (
    <>
      <div
        ref={listScrollRef}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`clip-table min-w-0 overflow-x-auto pb-16 transition-colors ${dragActive ? "bg-zinc-900/[0.03] ring-1 ring-inset ring-zinc-900/20" : ""
          }`}
      >
        {routeSearchId &&
          !activeSearchQuery.isError &&
          (activeSearchQuery.isLoading ||
            !activeSearch ||
            ["pending", "dispatched", "running", "retry"].includes(
              activeSearch.status,
            )) ? (
          <div className="px-8 py-16 text-center">
            <p className="text-base italic text-zinc-700">Searching your Clipboard…</p>
            <p className="mt-1 text-sm text-zinc-400">
              Translating your request into one read-only Clipboard command.
            </p>
          </div>
        ) : routeSearchId && activeSearchQuery.isError ? (
          <div className="px-8 py-16 text-center text-base text-red-700" role="alert">
            Could not load this search.
          </div>
        ) : routeSearchId && activeSearch?.status === "failed" ? (
          <div className="px-8 py-16 text-center" role="alert">
            <p className="text-base text-red-700">Search failed</p>
            <p className="mt-1 text-sm text-zinc-500">
              {activeSearch.error || "Please try the search again."}
            </p>
          </div>
        ) : routeSearchId &&
          activeSearch?.status === "complete" &&
          activeSearch.documents.length === 0 &&
          (activeSearch.folders?.length ?? 0) === 0 ? (
          <div className="px-8 py-16 text-center">
            <p className="text-base text-zinc-700">No matching files</p>
            <p className="mt-1 text-sm text-zinc-400">
              Try describing the files with broader concepts or different terms.
            </p>
          </div>
        ) : treeLoading && !tree ? (
          <div className="px-8 py-12 text-center text-base italic text-zinc-400">Loading…</div>
        ) : tree && tree.folders.length === 0 && tree.documents.length === 0 ? (
          <div className="mx-8 my-10 border border-dashed border-zinc-300 px-8 py-16 text-center">
            <p className="text-lg text-zinc-800">Nothing on the shelf yet.</p>
            <p className="mx-auto mt-2 max-w-md text-base leading-6 text-zinc-500">
              Drop PDFs, Word or PowerPoint files here, or add them from the list to
              the left. PDFs are parsed into full text;{" "}
              <code className="font-mono text-sm text-zinc-500">.bib</code> entries
              are matched to papers in the corpus.
            </p>
          </div>
        ) : (
          <table className="w-full min-w-[42rem] text-base leading-6" style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "60%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead>
              <tr className="font-ui border-b border-zinc-200 text-zinc-400 [&>th]:sticky [&>th]:top-0 [&>th]:bg-[var(--color-white,#fff)]">
                <th
                  className="px-8 py-2 text-left font-normal"
                  aria-sort={ariaSort("name")}
                >
                  <div className="flex items-center gap-3">
                    {selectableCount > 0 ? (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected && !allSelected;
                        }}
                        onChange={() =>
                          allSelected
                            ? clearSelection()
                            : setSelection(selectableDocIds, selectableFolderNames)
                        }
                        aria-label={allSelected ? "Clear selection" : "Select all"}
                        className="clip-check h-3.5 w-3.5 shrink-0 rounded-none border-zinc-400 text-zinc-900 focus:ring-0"
                      />
                    ) : (
                      <span className="w-3.5 shrink-0" />
                    )}
                    <button
                      type="button"
                      onClick={() => toggleSort("name")}
                      className="flex items-center gap-1 hover:text-zinc-700 focus-visible:outline-none focus-visible:text-zinc-900"
                    >
                      Name
                      {sortIcon("name")}
                    </button>
                  </div>
                </th>
                <th
                  className="px-3 py-2 text-left font-normal"
                  aria-sort={ariaSort("type")}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort("type")}
                    className="flex items-center gap-1 hover:text-zinc-700 focus-visible:outline-none focus-visible:text-zinc-900"
                  >
                    Type
                    {sortIcon("type")}
                  </button>
                </th>
                <th
                  className="px-8 py-2 text-left font-normal"
                  aria-sort={ariaSort("updated")}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort("updated")}
                    className="flex items-center gap-1 hover:text-zinc-700 focus-visible:outline-none focus-visible:text-zinc-900"
                  >
                    Updated
                    {sortIcon("updated")}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => {
                if (row.kind === "folder") {
                  const f = row.folder;
                  const target = f.path ?? (path ? `${path}/${f.name}` : f.name);
                  const fsel = selectedFolders.has(f.name);
                  return (
                    <tr
                      key={`folder-${f.name}`}
                      tabIndex={0}
                      className={`group cursor-pointer border-b border-zinc-200/70 transition-colors hover:bg-zinc-900/[0.025] focus-visible:outline-none focus-visible:bg-zinc-900/[0.025] ${fsel ? "bg-zinc-900/[0.04]" : ""}`}
                      onClick={() => {
                        navigate(
                          clipboardFolderUrl(
                            target,
                            f.shared && f.owner_id ? f.owner_id : sharedOwnerId,
                          ),
                        );
                        clearSelection();
                      }}
                      onKeyDown={(event) =>
                        activateRowOnKey(event, () => {
                          navigate(
                            clipboardFolderUrl(
                              target,
                              f.shared && f.owner_id ? f.owner_id : sharedOwnerId,
                            ),
                          );
                          clearSelection();
                        })
                      }
                    >
                      <td className="overflow-hidden px-8 py-2.5">
                        <div className="flex min-w-0 items-baseline gap-3">
                          {!f.shared && !routeSearchId ? (
                            <input
                              type="checkbox"
                              checked={fsel}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => toggleSelectFolder(f.name)}
                              aria-label={`Select folder ${f.name}`}
                              className="clip-check h-3.5 w-3.5 shrink-0 translate-y-[1px] rounded-none border-zinc-400 text-zinc-900 focus:ring-0"
                            />
                          ) : (
                            <span className="w-3.5 shrink-0" />
                          )}
                          <span className="min-w-0 truncate text-zinc-900">
                            {f.name}
                            <span className="text-zinc-400">/</span>
                          </span>
                          {f.shared && (
                            <span className="shrink-0 text-sm italic text-zinc-400">shared</span>
                          )}
                        </div>
                      </td>
                      <td className="truncate px-3 py-2.5 text-sm text-zinc-500">
                        Folder · {f.doc_count} file{f.doc_count === 1 ? "" : "s"}
                      </td>
                      <td className="px-8 py-2.5 text-sm text-zinc-400">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">{formatDate(f.latest_at)}</span>
                          {!f.shared && !routeSearchId && (
                            <span className="font-ui hidden max-lg:flex shrink-0 items-center gap-3 group-hover:flex group-focus-within:flex">
                              <button
                                type="button"
                                aria-label="Share folder"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSharingFolder(target);
                                }}
                                className="text-zinc-400 hover:text-zinc-900"
                              >
                                Share
                              </button>
                              <button
                                type="button"
                                aria-label="Delete folder"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  requestDeleteFolder(f.name, target, f.doc_count);
                                }}
                                className="text-zinc-400 hover:text-red-700"
                              >
                                Delete
                              </button>
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }
                const d = row.doc;
                const isSelected = selected.has(d.id);
                const isInspected = inspectedDoc?.id === d.id;
                const isLink = d.source_type === "link";
                return (
                  <tr
                    key={`doc-${d.id}`}
                    tabIndex={0}
                    className={`group cursor-pointer border-b border-zinc-200/70 transition-colors hover:bg-zinc-900/[0.025] focus-visible:outline-none focus-visible:bg-zinc-900/[0.025] ${isInspected ? "bg-zinc-900/[0.04] shadow-[inset_2px_0_0_var(--tw-shadow-color)] shadow-zinc-900" : isSelected ? "bg-zinc-900/[0.04]" : ""}`}
                    onClick={() => inspect(d)}
                    onKeyDown={(event) => activateRowOnKey(event, () => inspect(d))}
                  >
                    <td className="overflow-hidden px-8 py-2.5">
                      <div className="flex min-w-0 items-baseline gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleSelect(d.id)}
                          aria-label={`Select ${d.filename}`}
                          className="clip-check h-3.5 w-3.5 shrink-0 translate-y-[1px] rounded-none border-zinc-400 text-zinc-900 focus:ring-0"
                        />
                        <span className="min-w-0 flex-1">
                          <span className={`block truncate ${isLink ? "italic text-zinc-700" : "text-zinc-900"}`}>
                            {routeSearchId
                              ? d.title || d.filename
                              : d.filename || d.title}
                          </span>
                          {routeSearchId && (
                            <span className="block min-w-0 text-sm leading-5 text-zinc-400">
                              <span className="block truncate">
                                {d.filename}
                                {d.folder_path
                                  ? ` · Clipboard / ${d.folder_path}`
                                  : " · Clipboard / Root"}
                                {d.search_match?.match_count
                                  ? ` · ${d.search_match.match_count} match${d.search_match.match_count === 1 ? "" : "es"}`
                                  : ""}
                              </span>
                              {d.search_match?.snippet && (
                                <span className="block truncate text-zinc-500">
                                  {d.search_match.line_number ? (
                                    <span className="font-mono text-xs text-zinc-400">L{d.search_match.line_number} </span>
                                  ) : null}
                                  {d.search_match.snippet}
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td className="truncate px-3 py-2.5 text-sm text-zinc-500">
                      {clipboardDocumentFileType(d)}
                      {d.page_count ? <span className="text-zinc-400"> · {d.page_count} pp</span> : null}
                    </td>
                    <td className="px-8 py-2.5 text-sm text-zinc-400">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate">
                          {formatDate(d.updated_at || d.created_at)}
                        </span>
                        {!sharedOwnerId && !d.shared && !routeSearchId && (
                          <span className="font-ui hidden max-lg:flex shrink-0 items-center gap-3 group-hover:flex group-focus-within:flex">
                            {!isLink && (
                              <button
                                type="button"
                                aria-label={`Share ${d.filename || d.title || "file"}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSharingDoc({
                                    id: d.id,
                                    name: d.filename || d.title || "file",
                                  });
                                }}
                                className="text-zinc-400 hover:text-zinc-900"
                              >
                                Share
                              </button>
                            )}
                            <button
                              type="button"
                              aria-label={`Delete ${d.filename || d.title || "file"}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                requestDeleteDoc(d);
                              }}
                              className="text-zinc-400 hover:text-red-700"
                            >
                              Delete
                            </button>
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visibleCount < rows.length && (
                <tr ref={sentinelRef}>
                  <td colSpan={3} className="px-8 py-3 text-center text-sm italic text-zinc-400">
                    Loading more… ({rows.length - visibleCount} remaining)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {sharingFolder && (
        <SharePanel folderPath={sharingFolder} onClose={() => setSharingFolder(null)} />
      )}
      {sharingDoc && (
        <SharePanel
          documentId={sharingDoc.id}
          documentName={sharingDoc.name}
          onClose={() => setSharingDoc(null)}
        />
      )}
      <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
    </>
  );
};
